// Copyright (c) Ping. All rights reserved.
//
// Canonical accent palette shared by the Appearance pane (swatch row) and
// the web PreferencesStore (which maps the selected id → the `--ping-accent`
// custom property that the runtime accent-override stylesheet consumes).
//
// `default` reproduces the shipped brand colour (#537EFF — the `accent-brand`
// design token). Selecting it tells the store to REMOVE the accent override
// so the app renders the design-token palette exactly as shipped; every other
// id activates the override with its hex. Keeping one list here means the
// swatch the user taps and the colour the app repaints to can never drift.

export interface AccentOption {
  readonly id: string;
  readonly label: string;
  /** sRGB hex pushed into `--ping-accent` when this accent is active. */
  readonly hex: string;
}

/** Id of the shipped-default accent (brand blue, no override applied). */
export const DEFAULT_ACCENT_ID = 'default';

export const ACCENT_PALETTE: readonly AccentOption[] = [
  { id: 'default', label: 'Default', hex: '#537EFF' },
  { id: 'violet', label: 'Violet', hex: '#8D72EB' },
  { id: 'orchid', label: 'Orchid', hex: '#E663F4' },
  { id: 'rose', label: 'Rose', hex: '#FF5353' },
  { id: 'coral', label: 'Coral', hex: '#F58863' },
  { id: 'amber', label: 'Amber', hex: '#F5C451' },
  { id: 'green', label: 'Green', hex: '#3CCB4F' },
  { id: 'teal', label: 'Teal', hex: '#2CA8D4' },
];

/** Resolve an accent id to its hex, falling back to the brand default for
 *  unknown / legacy ids (e.g. the old dead-control default `indigo`). */
export function accentHex(id: string): string {
  return (
    ACCENT_PALETTE.find((a) => a.id === id)?.hex ??
    ACCENT_PALETTE[0]!.hex
  );
}

/** Whether an id maps to the shipped default (so the override is removed). */
export function isDefaultAccent(id: string): boolean {
  return id === DEFAULT_ACCENT_ID || ACCENT_PALETTE.every((a) => a.id !== id);
}
